<template>
  <div class="binity-slider-wrapper">
    <label v-if="label" :for="sliderId" class="binity-label">
      {{ label }}
    </label>

    <input
      :id="sliderId"
      type="range"
      :min="min"
      :max="max"
      :value="safeValue"
      @input="handleInput"
      @change="handleChange"
      class="binity-slider"
      :style="sliderStyle"
      :aria-valuenow="safeValue"
    />

    <span class="binity-value">{{ safeValue }}</span>
  </div>
</template>

<script>
export default {
  name: 'BinitySlider',
  props: {
    value: {
      type: [Number, String],
      default: null,
    },
    label: {
      type: String,
      default: 'Volume',
    },
    min: {
      type: [Number, String],
      default: 0,
    },
    max: {
      type: [Number, String],
      default: 100,
    },
  },
  data() {
    return {
      sliderId: 'binity-slider-' + Math.random().toString(36).substr(2, 9),
    }
  },
  computed: {
    safeValue() {
      return this.value == null ? Number(this.min) : Number(this.value)
    },
    sliderStyle() {
      const min = Number(this.min)
      const max = Number(this.max)
      const val = this.safeValue

      // Calculate percentage to fill the track correctly
      const percentage = ((val - min) / (max - min)) * 100

      return {
        '--slider-fill': `${percentage}%`,
      }
    },
  },
  methods: {
    handleInput(event) {
      this.$emit('input', Number(event.target.value))
    },
    handleChange(event) {
      this.$emit('change', Number(event.target.value))
    },
  },
}
</script>

<style scoped>
.binity-slider-wrapper {
  /* --- Theme Variables (Defaults to Shadcn colors) --- */
  --slider-primary: #0f172a;
  --slider-track: #e2e8f0;
  --slider-thumb: #ffffff;
  --slider-text-label: #020817;
  --slider-text-value: #64748b;

  display: flex;
  align-items: center;
  flex-direction: row;
  gap: 16px;
  width: 100%;
  margin: 10px;
}

.binity-label {
  font-size: 0.875rem; /* 14px */
  font-weight: 500;
  line-height: 1;
  color: var(--slider-text-label);
  cursor: default;
  white-space: nowrap;
  width: 150px;
}

.binity-value {
  font-size: 0.875rem;
  color: var(--slider-text-value);
  white-space: nowrap;
  min-width: 2.5rem;
  text-align: right;
}

.binity-slider {
  -webkit-appearance: none;
  appearance: none;
  flex: 1;
  width: 100%;
  height: 8px;
  border-radius: 9999px;
  background: linear-gradient(
    to right,
    var(--slider-primary) var(--slider-fill, 50%),
    var(--slider-track) var(--slider-fill, 50%)
  );
  outline: none;
  margin: 0;
  cursor: pointer;
}

.binity-slider:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.binity-slider::-webkit-slider-thumb {
  -webkit-appearance: none;
  appearance: none;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: var(--slider-thumb);
  border: 2px solid var(--slider-primary);
  cursor: grab;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  transition: background-color 0.15s ease, border-color 0.15s ease, box-shadow 0.15s ease;
}

.binity-slider::-webkit-slider-thumb:active {
  cursor: grabbing;
}

.binity-slider:focus-visible::-webkit-slider-thumb {
  outline: 2px solid transparent;
  outline-offset: 2px;
  box-shadow: 0 0 0 2px var(--slider-thumb), 0 0 0 4px var(--slider-primary);
}

.binity-slider::-moz-range-thumb {
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: var(--slider-thumb);
  border: 2px solid var(--slider-primary);
  cursor: grab;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  transition: background-color 0.15s ease, border-color 0.15s ease, box-shadow 0.15s ease;
}

.binity-slider::-moz-range-thumb:active {
  cursor: grabbing;
}

.binity-slider:focus-visible::-moz-range-thumb {
  outline: 2px solid transparent;
  outline-offset: 2px;
  box-shadow: 0 0 0 2px var(--slider-thumb), 0 0 0 4px var(--slider-primary);
}
</style>
